import React, { useEffect, useState, useCallback } from "react";
import { getDB, getOrCreateDeviceId } from "../utils";
import { useMessage } from "../components/MessageProvider";

function formatJson(value) {
	try {
		return JSON.stringify(value, null, 2);
	} catch (err) {
		return String(value);
	}
}

function DevTools({ onSetTopBarAction }) {
	const { showQuestion, showMessage } = useMessage();
	const [stores, setStores] = useState([]);
	const [loading, setLoading] = useState(true);
	const [selectedStore, setSelectedStore] = useState(null);
	const [records, setRecords] = useState([]);
	const [recordsLoading, setRecordsLoading] = useState(false);
	const [error, setError] = useState("");

	// Action button TopBar — icona info, apre popup di sistema
	useEffect(() => {
		if (!onSetTopBarAction) return;
		onSetTopBarAction({
			icon: (
				<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
					<circle cx="12" cy="12" r="10" />
					<path strokeLinecap="round" d="M12 8h.01M12 12v4" />
				</svg>
			),
			label: "Info DevTools",
			onClick: () => showMessage({
				title: "Sezione Sviluppo",
				message: "Strumenti di debug riservati agli sviluppatori. Qui puoi ispezionare la cache IndexedDB e testare i componenti UI.",
				type: "info",
				timeout: 0,
			}),
		});
		return () => onSetTopBarAction(null);
	}, [onSetTopBarAction, showMessage]);

	const loadStores = useCallback(async () => {
		setLoading(true);
		setError("");
		try {
			const db = await getDB();
			const names = Array.from(db.objectStoreNames);
			const meta = [];
			for (const name of names) {
				const count = await db.count(name);
				meta.push({ name, count });
			}
			setStores(meta);
			if (selectedStore && !names.includes(selectedStore)) {
				setSelectedStore(null);
				setRecords([]);
			}
		} catch (err) {
			console.error("Errore caricamento store IndexedDB", err);
			setError("Impossibile leggere l'indice IndexedDB.");
		} finally {
			setLoading(false);
		}
	}, [selectedStore]);

	useEffect(() => {
		loadStores();
	}, [loadStores]);

	const loadStoreRecords = async (storeName) => {
		setRecordsLoading(true);
		setError("");
		try {
			const db = await getDB();
			const tx = db.transaction(storeName, "readonly");
			const store = tx.objectStore(storeName);
			const [values, keys] = await Promise.all([store.getAll(), store.getAllKeys()]);
			const data = values.map((value, idx) => ({ key: keys[idx], value }));
			setSelectedStore(storeName);
			setRecords(data);
		} catch (err) {
			console.error("Errore lettura store", storeName, err);
			setError(`Impossibile leggere i record dello store ${storeName}.`);
		} finally {
			setRecordsLoading(false);
		}
	};

	const handleRefreshStore = async (storeName) => {
		await loadStoreRecords(storeName);
		await loadStores();
	};

	const handleDeleteRecord = async (storeName, key) => {
		const confirmDelete = window.confirm(`Eliminare il record ${key} dallo store ${storeName}?`);
		if (!confirmDelete) return;
		try {
			const db = await getDB();
			await db.delete(storeName, key);
			await handleRefreshStore(storeName);
		} catch (err) {
			console.error("Errore eliminazione record", err);
			setError("Impossibile eliminare il record selezionato.");
		}
	};

	const handleClearStore = async (storeName) => {
		const confirmClear = window.confirm(`Svuotare completamente lo store ${storeName}?`);
		if (!confirmClear) return;
		try {
			const db = await getDB();
			const tx = db.transaction(storeName, "readwrite");
			await tx.store.clear();
			await tx.done;
			await loadStores();
			if (selectedStore === storeName) {
				setRecords([]);
			}
		} catch (err) {
			console.error("Errore svuotamento store", err);
			setError("Impossibile svuotare lo store selezionato.");
		}
	};

	const handleTestMood = async () => {
		const isHappy = await showQuestion({
			title: "Mood check",
			message: "Sei contento?",
			confirmLabel: "Sì",
			cancelLabel: "No",
		});
		if (isHappy) {
			await showMessage({ title: "Mood", message: "FELICE", type: "success", timeout: 2 });
		} else {
			//Così proviamo entrambe le modalità
			await showMessage({ title: "Mood", message: "TRISTE", type: "error", timeout: 0 });
		}
	};

	const handleShowDeviceId = async () => {
		const id = getOrCreateDeviceId();
		await showMessage({ title: "UUID Dispositivo", message: id, type: "info", timeout: 0 });
	};

	return (
		<div
			style={{
				height: "calc(100dvh - 3.5rem - env(safe-area-inset-top))",
				overflowY: "auto",
				paddingBottom: "env(safe-area-inset-bottom)",
			}}
		>
		<div className="max-w-5xl mx-auto w-full px-3 py-6">
			<div className="flex items-center justify-between mb-4 gap-3">
				<h1 className="text-2xl font-bold text-blu-uss">Strumenti di sviluppo</h1>
				<div className="flex gap-2">
					<button
						type="button"
						className="px-4 py-2 rounded-lg bg-blue-100 text-blue-700 font-semibold hover:bg-blue-200"
						onClick={handleTestMood}
					>
						Test messaggi
					</button>
					<button
						type="button"
						className="px-4 py-2 rounded-lg bg-blue-100 text-blue-700 font-semibold hover:bg-blue-200"
						onClick={handleShowDeviceId}
					>
						UUID
					</button>
				</div>
			</div>
			<p className="text-sm text-gray-600 mb-6">
				Naviga i contenuti della cache IndexedDB. Puoi visualizzare, svuotare o eliminare singoli record in modo sicuro.
			</p>
			{error && <div className="mb-4 rounded-lg bg-red-100 text-red-700 px-4 py-2">{error}</div>}
			<div className="grid gap-4 md:grid-cols-2">
				{loading ? (
					<div className="col-span-2 text-center text-gray-500">Caricamento store...</div>
				) : stores.length === 0 ? (
					<div className="col-span-2 text-center text-gray-500">Nessuno store trovato.</div>
				) : (
					stores.map(store => (
						<div key={store.name} className={`rounded-xl border ${selectedStore === store.name ? "border-blu-uss" : "border-gray-200"} bg-white shadow-sm p-4 flex flex-col gap-2`}>
							<div className="flex items-center justify-between">
								<div>
									<h2 className="text-lg font-semibold text-gray-800">{store.name}</h2>
									<p className="text-xs text-gray-500">Record: {store.count}</p>
								</div>
								<div className="flex gap-2">
									<button
										type="button"
										className="px-3 py-1 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
										onClick={() => loadStoreRecords(store.name)}
									>
										Apri
									</button>
									<button
										type="button"
										className="px-3 py-1 text-sm rounded-lg border border-red-300 text-red-600 hover:bg-red-50"
										onClick={() => handleClearStore(store.name)}
									>
										Svuota
									</button>
								</div>
							</div>
						</div>
					))
				)}
			</div>

			<div className="mt-8">
				<h2 className="text-xl font-semibold text-gray-800 mb-3">Dettaglio store</h2>
				{selectedStore ? (
					<div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
						<div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
							<div>
								<p className="text-base font-bold text-blu-uss">{selectedStore}</p>
								<p className="text-xs text-gray-500">Record mostrati: {records.length}</p>
							</div>
							<div className="flex gap-2">
								<button
									type="button"
									className="px-3 py-1 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
									onClick={() => loadStoreRecords(selectedStore)}
								>
									Aggiorna
								</button>
								<button
									type="button"
									className="px-3 py-1 text-sm rounded-lg border border-red-300 text-red-600 hover:bg-red-50"
									onClick={() => handleClearStore(selectedStore)}
								>
									Svuota store
								</button>
							</div>
						</div>
						<div className="max-h-[420px] overflow-y-auto divide-y divide-gray-100">
							{recordsLoading ? (
								<div className="p-4 text-gray-500 text-center">Caricamento record...</div>
							) : records.length === 0 ? (
								<div className="p-4 text-gray-500 text-center">Nessun record presente.</div>
							) : (
								records.map(record => (
									<div key={record.key} className="p-4 flex flex-col gap-2">
										<div className="flex items-center justify-between text-xs text-gray-500">
											<span>Key: {String(record.key)}</span>
											<div className="flex gap-3">
												<button
													type="button"
													className="text-blue-500 hover:text-blue-700 font-semibold"
													onClick={() => navigator.clipboard.writeText(formatJson(record.value))}
												>
													Copia
												</button>
												<button
													type="button"
													className="text-red-500 hover:text-red-700 font-semibold"
													onClick={() => handleDeleteRecord(selectedStore, record.key)}
												>
													Elimina
												</button>
											</div>
										</div>
										<pre className="bg-gray-50 rounded-xl text-xs text-left text-gray-800 overflow-x-auto p-3 border border-gray-200">
											{formatJson(record.value)}
										</pre>
									</div>
								))
							)}
						</div>
					</div>
				) : (
					<div className="rounded-2xl border border-dashed border-gray-300 p-6 text-center text-gray-500">
						Seleziona uno store per visualizzare i record.
					</div>
				)}
			</div>
		</div>
		</div>
	);
}

export default DevTools;
